// The manufacturing line lists a section offers, built on the client off the polled manufacturing line scores --
// the manufacturing-line twin of the solutions grid's combineSolutionGroups.
//
// The server sends only the STRUCTURE (each part's manufacturing lines + the exact covers over the parts), because
// the full product across the parts is unbounded: a four-part cover of 11 manufacturing lines per part is
// 11**4 = 14,641 manufacturing line lists. The page lists only the section's `maximumLists` cheapest
// (SystemParam frank_maximum_manufacturing_line_lists), found without building the product (cheapestCombinations),
// and re-ranks them as the manufacturing line scores land. A manufacturing line with no score yet sorts LAST
// (Infinity).

import { cheapestCombinations, compareScores } from 'components/frank/utils/cheapestCombinations.js';

// The stable address of ONE distinct manufacturing line: "<sub_section_index>:<identifier>". The same line is
// reused across many lists, so its best score is filed and read once under this key (see bestScoreByLine). The
// ONE definition -- the component imports it, so the cap and the scoring can never key a line two different ways.
export const lineKey = (line) => `${line.sub_section_index}:${line.identifier}`;

// A manufacturing line's piece of a plan's identifier (Frank::ManufacturingLine#plan_identifier_piece): its identifier, or for
// a dynamically nested part set the identifier and the parts -- so a dynamic plan is never addressed as a
// template's on the same machines, nor as another dynamic plan that splits the parts differently.
export const planIdentifierPiece = (line) => line.plan_identifier_piece || line.identifier;

// repeatsTemplateCardsByLine: the manufacturing lines whose best-score answer says they offer only what the template
// cards of the same parts already do (Frank::RepeatsTemplateCards). A plan made only of them is a template plan again,
// so it is not listed; a plan with any manufacturing line that adds something is.
export const buildManufacturingLineLists = (subSections, exactCovers, bestScoreByLine, maximumLists, repeatsTemplateCardsByLine = {}) => {
  const scoreOf = (line) => {
    const score = bestScoreByLine[lineKey(line)];
    return typeof score === 'number' ? score : Infinity;
  };
  const totalOf = (manufacturingLines) => manufacturingLines.reduce(
    (sum, manufacturingLine) => sum + scoreOf(manufacturingLine), 0,
  );

  // A LIST IDENTIFIER BELONGS TO THE FIRST COVER THAT OFFERS IT. The identifier is only the manufacturing lines'
  // machines in cover order, so two covers of the same length can build the same one on different sub_sections --
  // and the server keeps the FIRST cover's (SectionManufacturingLineListsBuilder#manufacturing_line_lists), which is
  // the one it solves and prices. Keeping any other here would draw one assignment and solve another.
  const covers = exactCovers || [];
  const manufacturingLineIdentifiersByPart = subSections.map((subSection) => new Set(
    ((subSection && subSection.manufacturing_lines) || []).map(planIdentifierPiece),
  ));
  const offers = (cover, manufacturingLineIdentifiers) => cover.length === manufacturingLineIdentifiers.length
    && cover.every((partIndex, position) => (
      manufacturingLineIdentifiersByPart[partIndex] !== undefined
      && manufacturingLineIdentifiersByPart[partIndex].has(manufacturingLineIdentifiers[position])
    ));
  const ownedBy = (coverPosition, manufacturingLines) => {
    const manufacturingLineIdentifiers = manufacturingLines.map(planIdentifierPiece);
    return !covers.slice(0, coverPosition).some((earlierCover) => offers(earlierCover, manufacturingLineIdentifiers));
  };

  // Each cover's own cheapest `maximumLists` of the lists it owns -- enough that the section's cheapest
  // `maximumLists` are among them whichever covers they come from. A cover with a part no manufacturing line can
  // make offers nothing: cheapestCombinations returns no combination for an empty part.
  const candidates = [];
  covers.forEach((cover, coverPosition) => {
    const perPart = cover.map((index) => (subSections[index] && subSections[index].manufacturing_lines) || []);
    const owned = (manufacturingLines) => ownedBy(coverPosition, manufacturingLines)
      && !manufacturingLines.every((manufacturingLine) => repeatsTemplateCardsByLine[lineKey(manufacturingLine)] === true);
    cheapestCombinations(perPart, scoreOf, maximumLists, owned).forEach((manufacturingLines, rank) => {
      candidates.push({ manufacturingLines, total: totalOf(manufacturingLines), coverPosition, rank });
    });
  });
  candidates.sort((a, b) => compareScores(a.total, b.total) || a.coverPosition - b.coverPosition || a.rank - b.rank);

  const seen = new Set();
  const lists = [];
  candidates.forEach(({ manufacturingLines }) => {
    if (lists.length >= maximumLists) { return; }
    // Order matters: a list's identifier is its manufacturing lines' pieces joined in cover order
    // (Frank::ManufacturingLineList#identifier).
    const identifier = manufacturingLines.map(planIdentifierPiece).join('|');
    if (seen.has(identifier)) { return; }
    seen.add(identifier);
    lists.push({ identifier, manufacturing_lines: manufacturingLines });
  });
  return lists;
};

// The listing's complete plans: built from the STRUCTURE the server sends, or -- for a stored/cached response that
// predates the structure shape -- the pre-built lists it still carries.
export const manufacturingLineListsFromListing = (listing, bestScoreByLine, maximumLists, repeatsTemplateCardsByLine = {}) => {
  if (!listing) { return []; }
  if (Array.isArray(listing.sub_sections)) {
    return buildManufacturingLineLists(listing.sub_sections, listing.exact_covers, bestScoreByLine, maximumLists, repeatsTemplateCardsByLine);
  }
  return listing.manufacturing_line_lists || [];
};

// Every manufacturing line the section offers -- the whole candidate set (what the cap ranks), read from the
// structure or, for a legacy/cached payload, flattened out of the pre-built lists. Distinctness is the caller's
// (a line can recur across the pre-built lists); here it is just the flat set.
export const allManufacturingLinesOf = (listing) => {
  if (!listing) { return []; }
  if (Array.isArray(listing.sub_sections)) {
    return listing.sub_sections.flatMap((subSection) => subSection.manufacturing_lines || []);
  }
  return (listing.manufacturing_line_lists || []).flatMap((list) => list.manufacturing_lines || []);
};

// THE MANUFACTURING LINES ONE SCORING PASS ASKS ABOUT -- each once (the same manufacturing line recurs across
// lists), and only those with a sub_section_index, which is how a score is addressed (lineKey). The scoring pass
// and the readiness that waits for it both read this, so the page can never wait on a manufacturing line nobody
// asked about. A scoped manufacturing line list narrows it to that list's own; without one it is the section's.
export const manufacturingLinesToScore = (listing, scopedManufacturingLineList) => {
  const candidates = scopedManufacturingLineList
    ? (scopedManufacturingLineList.manufacturing_lines || [])
    : allManufacturingLinesOf(listing);
  const seen = new Set();
  return candidates.filter((manufacturingLine) => {
    if (manufacturingLine.sub_section_index == null) { return false; }
    const key = lineKey(manufacturingLine);
    if (seen.has(key)) { return false; }
    seen.add(key);
    return true;
  });
};

// Whether a plan (by its list identifier) is one the section can MAKE -- offered by some exact cover, each of the
// plan's lines belonging to that cover's part. Cap-INDEPENDENT: it reads every part's lines, not the bounded set,
// so a picked or URL-restored plan is judged by whether it can be built, not by whether it currently shows (the
// cap re-shuffles as scores land, and a valid pick must not be dropped just because it fell below the cap).
export const planOfferedByStructure = (listing, identifier) => Boolean(manufacturingLineListOf(listing, identifier));

// ONE manufacturing line list, by its identifier, built from the structure whether or not it is among the cheapest
// the listing shows -- so the one the user picked or priced can be kept on screen. null when no exact cover
// offers it (or for no listing / no identifier).
export const manufacturingLineListOf = (listing, identifier) => {
  if (!listing || !identifier) { return null; }
  if (!Array.isArray(listing.sub_sections)) {
    return (listing.manufacturing_line_lists || []).find((list) => list.identifier === identifier) || null;
  }
  const wantedIdentifiers = identifier.split('|');
  const manufacturingLineInPart = (partIndex, manufacturingLineIdentifier) => {
    const subSection = listing.sub_sections[partIndex];
    return ((subSection && subSection.manufacturing_lines) || [])
      .find((manufacturingLine) => planIdentifierPiece(manufacturingLine) === manufacturingLineIdentifier);
  };
  const cover = (listing.exact_covers || []).find((candidate) => (
    candidate.length === wantedIdentifiers.length
    && candidate.every((partIndex, position) => (
      Boolean(manufacturingLineInPart(partIndex, wantedIdentifiers[position]))
    ))
  ));
  if (!cover) { return null; }

  return {
    identifier,
    manufacturing_lines: cover.map(
      (partIndex, position) => manufacturingLineInPart(partIndex, wantedIdentifiers[position]),
    ),
  };
};
